/**
 * @file PanelStickyTop.tsx
 * @description Одна липкая строка сверху панели конструктора (phase19.16,
 * SB122; uiux-settings-groups §2): оглавление групп и/или ссылка возврата
 * («← К элементу» во «Шрифтах»). Вкладки, «Где действует» и индикатор
 * сохранения прокручиваются с содержимым — липкой остаётся только она.
 *
 * Строка сообщает свою высоту (`onHeight`): под неё встают заголовки групп
 * при переходе (`scroll-margin-top`) и липкий заголовок открытой группы на
 * телефоне (`--panel-sticky-h`).
 *
 * ```tsx
 * <PanelStickyTop><BackLink /></PanelStickyTop>
 * ```
 */

import { useEffect, useRef, type ReactNode } from "react";

import { cn } from "@/lib/utils";

interface Props {
  children: ReactNode;
  /** Высота строки, px — при каждом изменении. */
  onHeight?: (px: number) => void;
  className?: string;
}

export function PanelStickyTop({ children, onHeight, className }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !onHeight) return;
    const measure = () => onHeight(Math.round(el.getBoundingClientRect().height));
    measure();
    // В тестовом окружении (jsdom) наблюдателя размеров нет — хватит первого замера.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
      onHeight(0);
    };
  }, [onHeight]);

  return (
    <div
      ref={ref}
      data-slot="panel-sticky-top"
      className={cn("sticky top-0 z-10 flex min-w-0 shrink-0 flex-col bg-background [box-shadow:inset_0_-1px_0_var(--outline-variant)]", className)}
    >
      {children}
    </div>
  );
}
